import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

/**
 * Trust-signal chip — surfaces the aggregate count of junk posts the
 * Orange-Peel Floor has stopped BEFORE the paid LLM call across every
 * scan on the platform. Read-only aggregate; no PII.
 *
 * Silent if the endpoint fails or count falls below `minCount`.
 * `variant`:
 *   - "app"     → in-app subtle green pill (default)
 *   - "landing" → larger marketing chip for the landing hero
 */
export default function JunkFilteredChip({ minCount = 0, variant = "app" }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/stats/junk-filtered");
        if (!alive) return;
        const total = r.data?.total_dropped;
        if (typeof total === "number") setCount(total);
      } catch (_) {
        // silent
      }
    })();
    return () => { alive = false; };
  }, []);

  if (count === null || count < minCount) return null;

  const display = count >= 1000000
    ? `${(count / 1000000).toFixed(1)}M`
    : count >= 1000
      ? `${(count / 1000).toFixed(1)}k`
      : `${count}`;

  const isLanding = variant === "landing";

  return (
    <div
      data-testid={isLanding ? "junk-filtered-chip-landing" : "junk-filtered-chip"}
      title={`Orange-Peel has stopped ${count.toLocaleString()} junk posts before they cost a single LLM token.`}
      className={
        isLanding
          ? "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 backdrop-blur border border-lm-primary/20 text-xs md:text-sm font-mono text-lm-primary shadow-sm"
          : "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint2 border border-lm-tint2 text-[11px] font-mono text-lm-primary"
      }
    >
      <ShieldCheck className={isLanding ? "w-4 h-4" : "w-3 h-3"} strokeWidth={2.4} />
      <span>{display} junk posts filtered platform-wide</span>
    </div>
  );
}
